<script lang="ts" setup>
import ToolbarNav from "~/components/templates/v2/ToolbarNav/ToolbarNav.vue";
import type { IToolbarNavItemProps } from "~/components/templates/v2/ToolbarNav/ToolbarNav.models";
import { useShareInSocialNetwork } from "~/composables/useShareInSocialNetwork";
import { useClipboard } from "@vueuse/core";
import QRCode from "qrcode";
import {
  createSnackbarNotification,
  getSnowplowDataEventValue,
  Modal
} from "@frontiers-vue3/public-pages";

const props = withDefaults(
  defineProps<{ mobileItems?: IToolbarNavItemProps[] }>(),
  { mobileItems: () => [] }
);

const store = useMain();
const { currentResearchTopic, getParticipatingJournalsToSubmit } = store;
const {
  isOpenForColleagueReferral,
  isParticipateReferColleagueButtonVisible,
  baseUrl
} = useRuntimeConfig().public;
const { useNewParticipateUrl } = useEnvironmentConfigStore().getConfig();
const { getSocialShareLink } = useShareInSocialNetwork();
const route = useRoute();

const basePath = computed(
  () => `/research-topics/${route.params.rtId}/${currentResearchTopic.slug}`
);

const toggleSubmitManuscript = ref(false);

const isOpen =
  currentResearchTopic.isOpenForSubmission ??
  currentResearchTopic.isOpenForManuscriptSubmission ??
  false;

const shareUrl = getSocialShareLink("WeChat");
const showWeChatModal = ref(false);
const qrCodeCanvas = ref<HTMLCanvasElement | null>(null);
const { copy } = useClipboard();

const shareWeChatQR = async () => {
  showWeChatModal.value = !showWeChatModal.value;

  await nextTick();

  if (qrCodeCanvas.value) {
    await QRCode.toCanvas(qrCodeCanvas.value, shareUrl, { width: 200 });
  }
};

const shareWeChat = async () => {
  if (typeof window === "undefined") return;
  if (window.innerWidth > 1024) {
    await shareWeChatQR();
  } else {
    copy(window.location.href);

    createSnackbarNotification({
      message: "Link copied. Share the link on WeChat!",
      type: "success"
    });
  }
};

const basicInfoDrawerItems = computed<IToolbarNavItemProps[]>(() => {
  const items: IToolbarNavItemProps[] = [];

  if (store.getIsSubmitManuscriptEnabled) {
    items.push({
      label: "Submit",
      icon: "SendIcon",
      dataEvent: getSnowplowDataEventValue(
        "research_topic",
        "aside_click",
        "open_submission_dialog"
      ),
      dataResearchTopicIds: currentResearchTopic.id,
      onClick: () => {
        toggleSubmitManuscript.value = !toggleSubmitManuscript.value;
      }
    });
  }

  const submissionDeadline = new Date(
    currentResearchTopic.manuscriptSubmissionDeadline
  );

  if (
    isOpen &&
    !currentResearchTopic.closedOn &&
    isOpenForColleagueReferral &&
    isParticipateReferColleagueButtonVisible &&
    submissionDeadline >= new Date()
  ) {
    const participateSuffix = useNewParticipateUrl
      ? "participate-in-research-topic"
      : "participate-in-open-access-research-topic";

    items.push({
      label: "Participate",
      icon: "CheckmarkIcon",
      target: `participate-in-${currentResearchTopic.id}`,
      url: `${baseUrl}${basePath.value}/${participateSuffix}`,
      dataEvent: getSnowplowDataEventValue(
        "research_topic",
        "aside_click",
        "participate_in_research_topic"
      ),
      dataResearchTopicIds: currentResearchTopic.id
    });
  }

  items.push({
    label: "Share",
    icon: "ShareIcon",
    items: [
      {
        label: "Facebook",
        icon: "FacebookIcon",
        url: getSocialShareLink("Facebook"),
        dataEvent: getSnowplowDataEventValue(
          "research_topic",
          "aside_click",
          "share_on_facebook"
        ),
        dataResearchTopicIds: currentResearchTopic.id
      },
      {
        label: "X",
        icon: "XTwitterIcon",
        url: getSocialShareLink("X"),
        dataEvent: getSnowplowDataEventValue(
          "research_topic",
          "aside_click",
          "share_on_x"
        ),
        dataResearchTopicIds: currentResearchTopic.id
      },
      {
        label: "LinkedIn",
        icon: "LinkedinIcon",
        url: getSocialShareLink("LinkedIn"),
        dataEvent: getSnowplowDataEventValue(
          "research_topic",
          "aside_click",
          "share_on_linkedin"
        ),
        dataResearchTopicIds: currentResearchTopic.id
      },
      {
        label: "WeChat",
        icon: getSocialShareLink("WeChat"),
        onClick: shareWeChat,
        dataEvent: getSnowplowDataEventValue(
          "research_topic",
          "aside_click",
          "share_on_wechat"
        ),
        dataResearchTopicIds: currentResearchTopic.id
      }
    ]
  });

  return items;
});
</script>
<template>
  <aside class="Layout__asideRight">
    <ToolbarNav
      :items="basicInfoDrawerItems"
      :mobile-items="mobileItems" />

    <DialogSubmitManuscript
      v-if="store.getIsSubmitManuscriptEnabled"
      :participating-journals-to-submit="getParticipatingJournalsToSubmit"
      :is-dialog-open="toggleSubmitManuscript" />

    <Modal
      class="WeChatModal"
      title="Share on WeChat"
      heading="p"
      :force-show="showWeChatModal">
      <template #body>
        <div class="WeChatModal__message">
          <p>Scan with WeChat to share this article</p>
          <canvas
            ref="qrCodeCanvas"
            role="img"
            aria-label="QR code for sharing this page on WeChat"
            width="200"
            height="200"></canvas>
        </div>
      </template>
    </Modal>
  </aside>
</template>
<style lang="scss">
.WeChatModal {
  z-index: 2000;

  &__message {
    text-align: center;
  }
}
</style>
